import React, {useCallback, useState} from 'react'
import './MerchantModal.css'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, {SelectChangeEvent} from '@mui/material/Select'
import {
  AffiliateNetworkConfig,
  Merchant,
  MerchantAffiliateNetwork,
  MerchantAffiliateNetworkDisplayNames,
  MerchantCurrencyCode,
  MerchantPlan, MerchantPlanDisplayNames,
  MerchantPlatform,
  MerchantPlatformDisplayNames, MerchantPlatformDomains,
  MerchantStatus, MerchantStatusDisplayNames,
  NewMerchant
} from "../../types/merchants"
import {Button, FormControlLabel, FormHelperText, InputAdornment, Switch, TextField} from "@mui/material"
import {postMerchants} from "../../apis/postmerchants"
import {Nullable, Undefinable} from "../../types/utils"
import {patchMerchant} from "../../apis/patchmerchant"
import ImpactInputForm from './ImpactInputForm'
import CIQInputForm from './CiqInputForm'
import SocialSnowballInputForm from './SocialSnowballForm'
import EverflowInputForm from './EverflowInputForm'

interface MerchantModalProps {
  isOpen: boolean
  onClose: () => void
  merchantData: Nullable<Merchant>
  refreshTable: () => void
}

const MerchantModal: React.FC<MerchantModalProps> = ({isOpen, onClose, merchantData, refreshTable}) => {
  // Check to see if the modal is in edit mode based on if merchantData is passed in
  const isEditMode = Boolean(merchantData)
  
  // Define the initial state of the merchant based on if the modal is in edit mode or not
  const [merchant, setMerchant] = useState<NewMerchant>(() => {
    if (isEditMode && merchantData) {


      return {
        shop: merchantData.shop,
        status: merchantData.status,
        plan: merchantData.plan,
        platform: merchantData.platform,
        isCartless: merchantData.isCartless,
        affiliateNetworkConfig: merchantData.affiliateNetworkConfig,
      }
    } else {
      return {
        shop: '',
        status: MerchantStatus.ALLOWLISTED,
        plan: MerchantPlan.BASE,
        platform: MerchantPlatform.SHOPIFY,
        isCartless: false,
        affiliateNetworkConfig: {
          type: MerchantAffiliateNetwork.NONE,
        }
      }
    }
  })

  const handleAffiliateNetworkConfigChange = useCallback((newConfig: AffiliateNetworkConfig) => {
    setMerchant(prev => ({...prev, affiliateNetworkConfig: newConfig}))
  },[])

  const [validationErrors, setValidationErrors] = useState<string[]>([])

  if (!isOpen) return null

  // Define the available statuses to select from based on if the modal is in edit mode
  const allowedStatuses = isEditMode ? [MerchantStatus.ALLOWLISTED, merchant.status] : [MerchantStatus.ALLOWLISTED]

  // Define the available plans to select from based on if the modal is in edit mode
  const allowedPlans = isEditMode ? Object.values(MerchantPlan) : [MerchantPlan.BASE, MerchantPlan.INFLUENCER, MerchantPlan.ENTERPRISE, MerchantPlan.ENTERPRISE_INFLUENCER]

  const handlePlatformChange = (event: SelectChangeEvent) => {
    setMerchant(prev => ({ ...prev, platform: event.target.value as MerchantPlatform }))
  }

  const formatShop = (shop: string, platform: MerchantPlatform) => {
    let cleanShop = shop.replace(/^(http:\/\/|https:\/\/)/, '')
    const platformDomain = MerchantPlatformDomains[platform]

    if (cleanShop.includes(platformDomain) && platformDomain !== '') {
      const domainIndex = cleanShop.indexOf(platformDomain)
      cleanShop = cleanShop.substring(0, domainIndex)
    }

    return cleanShop
  }


  const handleShopChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let shop = formatShop(event.target.value, merchant.platform)

    setMerchant(prev => ({ ...prev, shop: shop }))
  }

  const handleStatusChange = (event: SelectChangeEvent) => {
    setMerchant(prev => ({ ...prev, status: event.target.value as MerchantStatus }))
  }

  const handlePlanChange = (event: SelectChangeEvent) => {
    setMerchant(prev => ({ ...prev, plan: event.target.value as MerchantPlan }))
  }

  const handleCartlessChange = () => {
    setMerchant(prev => ({...prev, isCartless: !prev.isCartless}))
  }

  const handleCurrencyCodeChange = (event: SelectChangeEvent) => {
    setMerchant(prev => ({ ...prev, currencyCode: event.target.value as MerchantCurrencyCode }))
  }

  const handleAffiliateNetworkChange = (event: SelectChangeEvent) => {
    const newAffiliateConfig = {
      type: event.target.value as MerchantAffiliateNetwork
    }
    setMerchant(prev => ({...prev, affiliateNetworkConfig: newAffiliateConfig}))
  }

  function validateMerchant(merchant: NewMerchant): string[] {
    const errors: string[] = []

    if (!isEditMode && merchant.platform === MerchantPlatform.FEED && !merchant.currencyCode) {
      errors.push('Shop currency is required for Feed merchants')
    }

    if (merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.IMPACT) {
      if (!merchant.affiliateNetworkConfig.impact?.accountSid.transportEncrypted) {
        errors.push('Impact Account SID is required')
      }
      if (!merchant.affiliateNetworkConfig.impact?.apiKey.transportEncrypted) {
        errors.push('Impact API key is required')
      }
      if (!merchant.affiliateNetworkConfig.impact?.programId) {
        errors.push('Impact program ID is required')
      }
    }

    if (merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.CIQ) {
      if (!merchant.affiliateNetworkConfig.ciq?.campaignId) {
        errors.push('CIQ Campaign ID is required')
      }
      if (!merchant.affiliateNetworkConfig.ciq?.partnerId) {
        errors.push('CIQ Partner ID is required')
      }
      if (!merchant.affiliateNetworkConfig.ciq?.apiKey.transportEncrypted) {
        errors.push('CIQ API key is required')
      }
    }

    if (merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.SOCIAL_SNOWBALL) {
      if (!merchant.affiliateNetworkConfig.socialSnowball?.email) {
        errors.push('Social Snowball email is required')
      }
      if (!merchant.affiliateNetworkConfig.socialSnowball?.password.transportEncrypted) {
        errors.push('Social Snowball password is required')
      }
      if (!merchant.isCartless) {
        errors.push('Social Snowball merchant must be cartless')
      }
    }

    if (merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.EVERFLOW) {
      if (!merchant.affiliateNetworkConfig.everflow?.networkApiKey.transportEncrypted) {
        errors.push('Everflow API key is required')
      }
      if (!merchant.affiliateNetworkConfig.everflow?.offerId) {
        errors.push('Everflow Offer ID is required')
      }
    }

    return errors
  }

  const showAlert = (message: string) => alert(message)

  const handleApiResponse = (response: Undefinable<{ status: number }>, successCode: number) => {
    if (response && response.status === successCode) {
      onClose()
      refreshTable()
    } else {
      showAlert('There was an error. Please try again.')
    }
  }

  const handleActionClick = async () => {
    //validate the merchant
    const errors = validateMerchant(merchant)
    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    try {
      let response
      if (isEditMode) {
        response = await patchMerchant(merchant)
        handleApiResponse(response, 200)
      } else {
        const completedMerchant = {
          ...merchant,
          shop: `${merchant.shop}${MerchantPlatformDomains[merchant.platform]}`,
        }
        response = await postMerchants(completedMerchant)
        handleApiResponse(response, 201)
      }
    } catch (e) {
      let action = isEditMode ? 'updating' : 'creating'
      showAlert(`There was an error ${action} the merchant. Please try again.`)
    }
  }
  
  return (
    <div className="modal-container">
      <div className="modal">
        <h2 className="modal-header">{isEditMode ? 'Update Merchant' : 'Create Merchant'}</h2>
        <FormControl className="formControlsContainer">
          <InputLabel>Platform</InputLabel>
          <Select
            value={merchant.platform}
            label="Platform"
            onChange={handlePlatformChange}
            disabled={isEditMode}
          >
            {Object.values(MerchantPlatform).map((platform) => (
              <MenuItem key={platform} value={platform}>
                {MerchantPlatformDisplayNames[platform]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className="formControlsContainer">
          <TextField
            value={formatShop(merchant.shop, merchant.platform)}
            onChange={handleShopChange}
            label='Shop'
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ color: 'gray' }}>{MerchantPlatformDomains[merchant.platform]}</span>
                </InputAdornment>
              ),
            }}
            disabled={isEditMode}
          />
        </FormControl>
        {!isEditMode && merchant.platform === MerchantPlatform.FEED && (
          <FormControl className="formControlsContainer">
            <InputLabel>Shop Currency</InputLabel>
            <Select
              value={merchant.currencyCode ?? ''}
              label="Shop Currency"
              onChange={handleCurrencyCodeChange}
            >
              {Object.values(MerchantCurrencyCode).map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl className="formControlsContainer">
          <InputLabel>Status</InputLabel>
          <Select
            value={merchant.status}
            label="Status"
            onChange={handleStatusChange}
          >
            {Object.values(allowedStatuses).map((status) => (
              <MenuItem key={status} value={status}>
                {MerchantStatusDisplayNames[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className="formControlsContainer">
          <InputLabel>Plan</InputLabel>
          <Select
            value={merchant.plan}
            label="Plan"
            onChange={handlePlanChange}
          >
            {Object.values(allowedPlans).map((plan) => (
              <MenuItem key={plan} value={plan}>
                {MerchantPlanDisplayNames[plan]}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            By selecting a plan other than "{MerchantPlanDisplayNames[MerchantPlan.NONE]}" you are granting
            access to the product without charging through the {MerchantPlatformDisplayNames[merchant.platform]} ecosystem.
          </FormHelperText>
        </FormControl>
        <FormControl className="formControlsContainer">
          <InputLabel>Affiliate Network</InputLabel>
          <Select
            value={merchant.affiliateNetworkConfig.type}
            label="Affiliate Network"
            onChange={handleAffiliateNetworkChange}
          >
            {Object.values(MerchantAffiliateNetwork).map((platform) => (
              <MenuItem key={platform} value={platform}>
                {MerchantAffiliateNetworkDisplayNames[platform]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {
          merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.IMPACT &&
          <ImpactInputForm
            handleAffiliateNetworkConfigChange={handleAffiliateNetworkConfigChange}
            existingConfig={merchant.affiliateNetworkConfig}
          />
        }
        {
          merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.CIQ &&
          <CIQInputForm
             handleAffiliateNetworkConfigChange={handleAffiliateNetworkConfigChange}
             existingConfig={merchant.affiliateNetworkConfig}
          />
        }
        {
          merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.SOCIAL_SNOWBALL &&
          <SocialSnowballInputForm
             handleAffiliateNetworkConfigChange={handleAffiliateNetworkConfigChange}
             existingConfig={merchant.affiliateNetworkConfig}
          />
        }
        {
          merchant.affiliateNetworkConfig.type === MerchantAffiliateNetwork.EVERFLOW &&
          <EverflowInputForm
             handleAffiliateNetworkConfigChange={handleAffiliateNetworkConfigChange}
             existingConfig={merchant.affiliateNetworkConfig}
          />
        }
        <FormControlLabel
            control={
              <Switch
                checked={merchant.isCartless}
                onChange={handleCartlessChange}
              />
            }
            label="Is Cartless?"
            sx={{
              mb: 3,
              ml: '0px',
              borderRadius: '5px',
              width: '200px',
              outline: '1px solid lightgrey',

            }}
          />

          {/* validation errors */}
          {validationErrors.length > 0 && (
            <ul style={{ color: 'red', marginBottom: '1rem' }}>
              {validationErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          )}

        {/* Create and Cancel Buttons */}
        <div className="modal-actions">
          <Button className="create-merchant-button" variant="contained" onClick={handleActionClick}>
            {isEditMode ? 'Update Merchant' : 'Create Merchant'}
          </Button>
          <Button className="cancel-merchant-button" onClick={onClose}>
            Cancel
          </Button>
        </div>

      </div>
    </div>
  )
}

export default MerchantModal
